"use client";

import React from "react";
import { SkinnedMeshRenderer } from "../types/avatar";
import { defaultSkinnedMesh } from "./DefaultSkinnedMesh";
import { ThuyKieuSkinnedMesh } from "./ThuyKieuSkinnedMesh";

/**
 * Scan GLTF nodes to build a mesh config automatically.
 * Finds root bones (isBone without a bone parent) and all SkinnedMesh nodes.
 */
const createAutoMeshConfig = (nodes: Record<string, any>) => {
  const nodeEntries = Object.entries(nodes || {});

  const primitives = nodeEntries
    .filter(([, node]) => node?.isBone && (!node.parent || !node.parent.isBone))
    .map(([name]) => name);

  const meshes = nodeEntries
    .filter(([, node]) => node?.isSkinnedMesh && node?.geometry && node?.skeleton)
    .map(([name, node]) => ({
      node: name,
      material: node?.material?.name,
      morphTargets: !!node?.morphTargetDictionary,
    }));

  return { primitives, meshes };
};

/**
 * Render avatar by iterating a meshConfig object containing primitives + meshes.
 * Returns null if config is invalid so callers can fall back to a preset renderer.
 */
const renderAvatarByMeshConfig = (
  nodes: Record<string, any>,
  materials: Record<string, any>,
  meshConfig: any
): React.ReactElement | null => {
  if (!meshConfig || typeof meshConfig !== "object") return null;

  const primitives: string[] = Array.isArray(meshConfig.primitives) ? meshConfig.primitives : [];
  const meshes: any[] = Array.isArray(meshConfig.meshes) ? meshConfig.meshes : [];

  return (
    <>
      {primitives.map((primitiveName, index) => {
        const node = nodes?.[primitiveName];
        if (!node) return null;
        return <primitive key={`primitive-${primitiveName}-${index}`} object={node} />;
      })}

      {meshes.map((mesh, index) => {
        const nodeName = mesh?.node;
        if (!nodeName) return null;

        const node = nodes?.[nodeName];
        if (!node || !node.geometry || !node.skeleton) return null;

        const resolvedMaterial =
          (mesh?.material && materials?.[mesh.material]) || node.material;
        const enableMorphTargets = mesh?.morphTargets ?? !!node.morphTargetDictionary;

        return (
          <skinnedMesh
            key={`mesh-${nodeName}-${index}`}
            name={node.name || nodeName}
            geometry={node.geometry}
            material={resolvedMaterial}
            skeleton={node.skeleton}
            morphTargetDictionary={enableMorphTargets ? node.morphTargetDictionary : undefined}
            morphTargetInfluences={enableMorphTargets ? node.morphTargetInfluences : undefined}
          />
        );
      })}
    </>
  );
};

/**
 * Factory that returns the correct SkinnedMeshRenderer based on meshPreset.
 *
 * Supports 3 presets:
 * - 'wolf3d'   → DefaultSkinnedMesh (ReadyPlayerMe models)
 * - 'thuykieu' → ThuyKieuSkinnedMesh (custom Thúy Kiều model)
 * - 'auto'     → Auto-detect from GLTF nodes; falls back to DefaultSkinnedMesh
 *
 * @param meshPreset - One of 'wolf3d' | 'thuykieu' | 'auto'
 * @param meshConfig - Optional pre-built mesh config (from Admin). Used with 'auto' preset.
 */
export const getSkinnedMeshRenderer = (
  meshPreset: string | undefined,
  meshConfig?: any
): SkinnedMeshRenderer => {
  const renderAuto = (nodes: any, materials: any, props: any, group: any) => {
    const config = meshConfig || createAutoMeshConfig(nodes);
    const rendered = renderAvatarByMeshConfig(nodes, materials, config);
    if (rendered) {
      return (
        <group {...props} dispose={null} ref={group}>
          {rendered}
        </group>
      );
    }

    // Safe fallbacks if auto fails
    if (nodes.Wolf3D_Body || nodes.Wolf3D_Head) {
      return defaultSkinnedMesh(nodes, materials, props, group);
    }
    if (nodes.AoDaiBK001 || nodes.body002) {
      return ThuyKieuSkinnedMesh(nodes, materials, props, group);
    }
    return null;
  };

  switch (meshPreset) {
    case "thuykieu":
      return (nodes, materials, props, group) => {
        try {
          // Attempt to render with the specific preset
          return ThuyKieuSkinnedMesh(nodes, materials, props, group);
        } catch (err) {
          console.warn("ThuyKieu preset selected but required nodes are missing, trying wolf3d...", err);
          try {
            return defaultSkinnedMesh(nodes, materials, props, group);
          } catch (err2) {
            console.warn("Wolf3D fallback also failed, falling back to auto", err2);
            return renderAuto(nodes, materials, props, group);
          }
        }
      };

    case "auto":
      return renderAuto;

    case "wolf3d":
    default:
      return (nodes, materials, props, group) => {
        try {
          // Attempt to render with the specific preset
          return defaultSkinnedMesh(nodes, materials, props, group);
        } catch (err) {
          console.warn("Wolf3D preset selected but required nodes are missing, trying thuykieu...", err);
          try {
            return ThuyKieuSkinnedMesh(nodes, materials, props, group);
          } catch (err2) {
            console.warn("ThuyKieu fallback also failed, falling back to auto", err2);
            return renderAuto(nodes, materials, props, group);
          }
        }
      };
  }
};
